import { defineAsyncComponent, defineComponent } from 'vue';
import * as constants from '@/common/constants';
const AdminHome = defineAsyncComponent(() => import(/* webpackChunkName: "admin" */ '@/client/components/admin/AdminHome.vue'));
const CardList = defineAsyncComponent(() => import(/* webpackChunkName: "card-list" */ '@/client/components/cardlist/CardList.vue'));
const CreateGameForm = defineAsyncComponent(() => import(/* webpackChunkName: "create-game" */ '@/client/components/create/CreateGameForm.vue'));
const GameEnd = defineAsyncComponent(() => import(/* webpackChunkName: "game-end" */ '@/client/components/GameEnd.vue'));
const GameHome = defineAsyncComponent(() => import(/* webpackChunkName: "game-home" */ '@/client/components/GameHome.vue'));
const GamesOverview = defineAsyncComponent(() => import(/* webpackChunkName: "games-overview" */ '@/client/components/GamesOverview.vue'));
const Help = defineAsyncComponent(() => import(/* webpackChunkName: "help" */ '@/client/components/help/Help.vue'));
const JudgeHome = defineAsyncComponent(() => import(/* webpackChunkName: "judge-home" */ '@/client/components/judge/JudgeHome.vue'));
const LoginHome = defineAsyncComponent(() => import(/* webpackChunkName: "login" */ '@/client/components/auth/LoginHome.vue'));
const LoadGameForm = defineAsyncComponent(() => import(/* webpackChunkName: "load-game" */ '@/client/components/LoadGameForm.vue'));
const PlayerHome = defineAsyncComponent(() => import(/* webpackChunkName: "player-home" */ '@/client/components/PlayerHome.vue'));
const SpectatorHome = defineAsyncComponent(() => import(/* webpackChunkName: "spectator-home" */ '@/client/components/SpectatorHome.vue'));
const ReplayScreen = defineAsyncComponent(() => import(/* webpackChunkName: "replay" */ '@/client/components/replay/ReplayScreen.vue'));
const StartScreen = defineAsyncComponent(() => import(/* webpackChunkName: "start-screen" */ '@/client/components/StartScreen.vue'));
import { $t, setTranslationContext } from '@/client/directives/i18n';
import { paths } from '@/common/app/paths';
import { statusCode } from '@/common/http/statusCode';
import { passwordParam } from '@/client/utils/playerPassword';
/** A player seat already claimed by someone else. */
class UnauthorizedError extends Error {
}
import { isJudgeId, isPlayerId, isSpectatorId } from '@/common/Types';
import { hasShowModal, showModal, windowHasHTMLDialogElement } from './HTMLDialogElementCompatibility';
import dialogPolyfill from 'dialog-polyfill';
import { setDocumentTitle } from '../utils/documentTitle';
import { reloadForChunkError } from '@/client/utils/chunkReload';
import BannerStack from '@/client/components/replay/BannerStack.vue';
import { localPlayerView } from '@/client/utils/localColors';
// NOTE: this simplistic truncation to the last segment might cause issues if
// this page starts supporting paths more than one level deep.
function getLastPathSegment() {
    // Leave only the last part of /path
    return window.location.pathname.replace(/.*\//g, '');
}
export default defineComponent({
    name: 'App',
    data() {
        return {
            screen: 'empty',
            isServerSideRequestInProgress: false,
            componentsVisibility: {
                'milestones': true,
                'awards_list': true,
                'tags_concise': false,
                'pinned_player_0': false,
                'pinned_player_1': false,
                'pinned_player_2': false,
                'pinned_player_3': false,
                'pinned_player_4': false,
                'turmoil_parties': false,
            },
            game: undefined,
            playerView: undefined,
            spectator: undefined,
            judge: undefined,
            login: undefined,
            replayParticipantId: undefined,
        };
    },
    components: {
        BannerStack,
        StartScreen,
        CreateGameForm,
        LoadGameForm,
        GameHome,
        PlayerHome,
        SpectatorHome,
        JudgeHome,
        GameEnd,
        GamesOverview,
        CardList,
        Help,
        AdminHome,
        LoginHome,
        ReplayScreen,
    },
    computed: {
        participant() {
            return this.playerView ?? this.spectator;
        },
        /** The judge has frozen the game that this page shows live. A replay shows a past moment, which is never frozen. */
        frozen() {
            switch (this.screen) {
                case 'player-home':
                    return this.playerView?.game.frozen === true;
                case 'spectator-home':
                    return this.spectator?.game.frozen === true;
                case 'judge-home':
                    return this.judge?.game.frozen === true;
                default:
                    return false;
            }
        },
        /** A game with Player undo keeps its history on the game page, where a solo player rewinds it and a table votes on it, judge or not. */
        hasHistory() {
            return this.playerView?.game.gameOptions.undoOption === true;
        },
        /** A game with Judge undo keeps its history on the judge page, where the judge rewinds and freezes it. */
        judgeHasHistory() {
            return this.judge?.game.gameOptions.judgeUndo === true;
        },
    },
    methods: {
        showAlert(title, message, cb = () => { }) {
            const dialogElement = document.getElementById('alert-dialog');
            const buttonElement = document.getElementById('alert-dialog-button');
            const messageElement = document.getElementById('alert-dialog-message');
            const titleElement = document.getElementById('alert-dialog-title');
            if (buttonElement !== null && titleElement !== null && messageElement !== null && dialogElement !== null && hasShowModal(dialogElement)) {
                messageElement.innerHTML = $t(message);
                titleElement.textContent = $t(title);
                const handler = () => {
                    buttonElement.removeEventListener('click', handler);
                    cb();
                };
                buttonElement.addEventListener('click', handler);
                showModal(dialogElement);
            }
            else {
                alert(message);
                cb();
            }
        },
        setVisibilityState(targetVar, isVisible) {
            if (isVisible === this.getVisibilityState(targetVar)) {
                return;
            }
            this.componentsVisibility[targetVar] = isVisible;
        },
        getVisibilityState(targetVar) {
            return this.componentsVisibility[targetVar] ? true : false;
        },
        /**
         * Fetches and shows the latest player, spectator or judge view.
         *
         * Resolves to false when the fetch fails.
         */
        update(path) {
            const currentPathname = getLastPathSegment();
            const app = this;
            const url = 'api/' + path + window.location.search.replace('&noredirect', '');
            return fetch(url)
                .then((resp) => {
                if (resp.status === statusCode.unauthorized) {
                    throw new UnauthorizedError();
                }
                if (!resp.ok) {
                    throw new Error(`Error getting game data: ${resp.statusText}`);
                }
                return resp.json();
            })
                .then((model) => {
                if (path === paths.PLAYER) {
                    app.playerView = localPlayerView(model);
                    setTranslationContext(app.playerView);
                }
                else if (path === paths.SPECTATOR) {
                    app.spectator = model;
                }
                else {
                    app.judge = model;
                    setTranslationContext(app.judge);
                }
                // The password has to survive into the address bar: it is how this
                // seat or judge page stays claimed, and every later poll reads it back out of the URL.
                const password = passwordParam(model);
                // A finished judge game stays on the judge page, which has no results screen.
                if (path !== paths.JUDGE &&
                    model.game.phase === 'end' &&
                    window.location.search.includes('&noredirect') === false) {
                    app.screen = 'the-end';
                    if (currentPathname !== paths.THE_END) {
                        window.history.replaceState(model, `${constants.APP_NAME} - Player`, `${paths.THE_END}?id=${model.id}${password}`);
                    }
                }
                else {
                    if (path === paths.PLAYER) {
                        app.screen = 'player-home';
                    }
                    else if (path === paths.SPECTATOR) {
                        app.screen = 'spectator-home';
                    }
                    else {
                        app.screen = 'judge-home';
                    }
                    // The server may hand a player or judge a different password than the address bar holds
                    // (a re-claimed seat), and polls would then be refused. A spectator model has no password.
                    const stalePassword = password !== '' &&
                        new URLSearchParams(window.location.search).get('password') !== model.password;
                    if (currentPathname !== path || stalePassword) {
                        window.history.replaceState(model, `${constants.APP_NAME} - Game`, `${path}?id=${model.id}${password}`);
                    }
                }
                return true;
            })
                .catch((err) => {
                if (err instanceof UnauthorizedError) {
                    if (path === paths.JUDGE) {
                        alert($t('Incorrect judge password. Reopen the judge page from your browser history, or ask the organizer for the judge link with its password.'));
                        return false;
                    }
                    alert('Incorrect password.\n(If you accidentally closed your player page, check your browser\'s History > Recently Closed tabs.)');
                    return false;
                }
                alert('Error getting game data');
                console.error(err);
                return false;
            });
        },
        updatePlayer() {
            return this.update(paths.PLAYER);
        },
        updateSpectator() {
            return this.update(paths.SPECTATOR);
        },
        updateJudge() {
            return this.update(paths.JUDGE);
        },
    },
    mounted() {
        setDocumentTitle();
        if (!windowHasHTMLDialogElement()) {
            dialogPolyfill.registerDialog(document.getElementById('alert-dialog'));
        }
        const currentPathname = getLastPathSegment();
        const app = this;
        if (currentPathname === paths.PLAYER) {
            app.updatePlayer();
        }
        else if (currentPathname === paths.THE_END) {
            const urlParams = new URLSearchParams(window.location.search);
            const id = urlParams.get('id') || '';
            if (isPlayerId(id)) {
                app.updatePlayer();
            }
            else if (isSpectatorId(id)) {
                app.updateSpectator();
            }
            else {
                alert('Bad id URL parameter.');
            }
        }
        else if (currentPathname === paths.REPLAY) {
            const id = new URLSearchParams(window.location.search).get('id') || '';
            if (isPlayerId(id) || isSpectatorId(id) || isJudgeId(id)) {
                app.replayParticipantId = id;
                app.screen = 'replay';
            }
            else {
                alert('Bad id URL parameter.');
            }
        }
        else if (currentPathname === paths.GAME) {
            const url = paths.API_GAME + window.location.search;
            fetch(url)
                .then((resp) => {
                if (!resp.ok) {
                    throw new Error(`Error getting game data: ${resp.statusText}`);
                }
                return resp.json();
            })
                .then((appGame) => {
                app.screen = 'game-home';
                app.game = appGame;
                window.history.replaceState(appGame, `${constants.APP_NAME} - Game`, `${paths.GAME}?id=${appGame.id}`);
            })
                .catch((err) => {
                alert('Error getting game data');
                console.error(err);
            });
        }
        else if (currentPathname === paths.GAMES_OVERVIEW) {
            app.screen = 'games-overview';
        }
        else if (currentPathname === paths.NEW_GAME) {
            app.screen = 'create-game-form';
        }
        else if (currentPathname === paths.LOAD) {
            app.screen = 'load';
        }
        else if (currentPathname === paths.CARDS) {
            app.screen = 'cards';
        }
        else if (currentPathname === paths.HELP) {
            app.screen = 'help';
        }
        else if (currentPathname === paths.SPECTATOR) {
            app.updateSpectator();
        }
        else if (currentPathname === paths.JUDGE) {
            app.updateJudge();
        }
        else if (currentPathname === paths.ADMIN) {
            app.screen = 'admin';
        }
        else if (currentPathname === paths.LOGIN) {
            app.screen = 'login-home';
        }
        else {
            app.screen = 'start-screen';
        }
    },
    // A lazy-loaded screen that fails to load lands here. In production builds Vue only logs the
    // error, so it never shows up as an unhandled rejection.
    errorCaptured(err) {
        reloadForChunkError(err);
    },
});
